import React from 'react'
import { connect } from 'react-redux'
import {
  CollectionType,
  EntityType,
  NegotiationActions,
  NegotiationEventAcceptBulkNegotiation,
  NegotiationType,
  ownOrderToSide,
  RfqType
} from 'api'
import { IsBestQuote, NegotiationDetailWithMeta } from 'api/local'
import { ActionItem, SplitButtonDropdown, SplitButtonType } from 'view/components/menus'
import Ticket from 'view/routes/requests/rfqTicket'
import { ReadOnlyCollateralBasketDetailsButton } from 'view/shared/collateralBasket/ReadOnlyCollateralBasketDetailsButton'
import { IndicativeContainer } from 'view/shared/datagrid'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { BulkPriceDialog } from './BulkPriceDialog'
import { Locale, Translation } from 'locale'
import {
  canAccept,
  canBackToStaging,
  canCancel,
  canRefresh,
  canReject,
  canRevert,
  canSend,
  canUpdateAttributes,
  canWithdraw,
  getLiveStatusText,
  getNegotiatedValue,
  getRFQTitle,
  isNegotiationHistory,
  isQuotable,
  isSummaryNegotiationType,
  shouldHideAccept
} from 'view/routes/requests/shared/utils'
import { WindowActions } from 'epics/windowEpic'
import { getValidUntilTime, isOpenFin } from 'utils'
import { TradingEntityWithAliasHelper } from 'model'
import { createStructuredSelector } from 'reselect'
import { createIsInitiatedNegotiationCounterpartyViewEnabled } from 'selectors/userPreferenceSelectors'
import { serverClientTimeOffsetSelector } from 'selectors/timeSelectors'
import {
  hasInitiatedCollectionOnlyOneCounterpartySelector,
  hasInitiatedNegotiationCollectionAnyBestPriceSelector,
  initiatedNegotiationsCountByCollectionIdGetterSelector,
  initiatedNegotiationsCountByRfqIdGetterSelector
} from 'selectors/negotiations'
import { iconSet } from 'view/components/icons'
import { OptionalTestProps } from 'view/components/datagrid'
import { GridApi } from 'ag-grid-community'
import { InitiatedGroupByOptions } from '../initiated/InitiatedGroupByToggles'

export interface LiveActionsButtonProps extends ModalConsumerProps, OptionalTestProps {
  isGroupRow: boolean
  disabled?: boolean
  gridApi?: GridApi
  isCounterpartyNegotiation: boolean
  negotiationDetails: NegotiationDetailWithMeta[]
  isCounterpartyViewEnabled: boolean
  serverClientTimeOffset: number
  enabledActionIds?: string[]
  groupByOption?: InitiatedGroupByOptions | null
  hasInitiatedCollectionOnlyOneCounterparty: (collectionId: string) => boolean
  hasCollectionAnyBestPrice: (collectionId: string) => boolean
  getNegotiationCountWithRfqId: (rfqId: string) => number
  getNegotiationCountWithCollectionId: (collectionId: string) => number
  onQuote: (negotiationId: string, ownOrder: boolean, otwDurationMins: string | null, isLastLook: boolean) => unknown
  onCounter: (negotiationId: string, ownOrder: boolean, refresh: boolean) => unknown
  onInitiatorRevert: (negotiationId: string, ownOrder: boolean) => unknown
  onResponderRevert: (negotiationId: string, ownOrder: boolean) => unknown
  onWithdraw: (negotiationId: string, ownOrder: boolean) => unknown
  onAccept: (negotiations: NegotiationEventAcceptBulkNegotiation[]) => unknown
  onReject: (negotiationId: string, ownOrder: boolean) => unknown
  onCancelBulk: (negotiationIds: string[], cloneOriginalOrder: boolean) => unknown
  onBackToStaging: (negotiationId: string, ownOrder: boolean) => unknown
  onOpenTicket: (entityId: string, entityType: EntityType, windowTitle: string, ownOrder: boolean, history: boolean) => unknown
}

const mapDispatchToProps = {
  onQuote: NegotiationActions.quote,
  onCounter: NegotiationActions.counter,
  onInitiatorRevert: NegotiationActions.initiatorRevert,
  onResponderRevert: NegotiationActions.responderRevert,
  onWithdraw: NegotiationActions.withdraw,
  onAccept: NegotiationActions.acceptBulk,
  onReject: NegotiationActions.reject,
  onCancelBulk: NegotiationActions.cancelBulk,
  onBackToStaging: NegotiationActions.backToStaging,
  onOpenTicket: WindowActions.openTicketWindow
}

const mapStateToProps = createStructuredSelector({
  hasCollectionAnyBestPrice: hasInitiatedNegotiationCollectionAnyBestPriceSelector,
  hasInitiatedCollectionOnlyOneCounterparty: hasInitiatedCollectionOnlyOneCounterpartySelector,
  isCounterpartyViewEnabled: createIsInitiatedNegotiationCounterpartyViewEnabled(),
  serverClientTimeOffset: serverClientTimeOffsetSelector,
  getNegotiationCountWithRfqId: initiatedNegotiationsCountByRfqIdGetterSelector,
  getNegotiationCountWithCollectionId: initiatedNegotiationsCountByCollectionIdGetterSelector
})

const COUNTERPARTY_ACCEPT = `${Translation.TC_ACTION_ACCEPT}_Counterparty`
const COUNTERPARTY_REJECT = `${Translation.TC_ACTION_REJECT}_Counterparty`

export class LiveActionsButtonComponent extends React.Component<LiveActionsButtonProps> {
  render() {
    const { disabled, isGroupRow, isCounterpartyViewEnabled, dataTestId } = this.props
    const actions = this.getActions()
    const doNegotiationsHaveBaskets = this.props.negotiationDetails.some(negotiationDetails => negotiationDetails.collateralBasket)
    const isIndicative = this.props.negotiationDetails.every(
      negotiationDetail => getNegotiatedValue(negotiationDetail.negotiation.rfqType) === RfqType.INDICATIVE
    )

    return (
      <>
        <SplitButtonDropdown
          dataTestId={dataTestId ?? 'LiveActions'}
          actions={actions}
          disabled={disabled}
          maxButtons={1}
          showDisabledActions={isGroupRow}
          withPortal
        />
        {doNegotiationsHaveBaskets && !this.props.isGroupRow && (
          <ReadOnlyCollateralBasketDetailsButton
            collateralBasketId={this.props.negotiationDetails[0].collateralBasket?.id}
            data-test-id={`BasketDetailsButton-${dataTestId}`}
          />
        )}
        {isIndicative && !this.props.isGroupRow && (!isCounterpartyViewEnabled || !this.props.negotiationDetails[0].ownOrder) && (
          <IndicativeContainer>{iconSet.indicativeIcon}</IndicativeContainer>
        )}
      </>
    )
  }

  private getWarnings = () => {
    const { negotiationDetails } = this.props
    const isBestQuote = negotiationDetails[0].meta?.isBestQuote

    if (isBestQuote === IsBestQuote.YES) {
      return []
    }

    return [
      isBestQuote === IsBestQuote.NO
        ? Locale.translate(Translation.SC_ACTION_NOT_BEST_PRICE)
        : Locale.translate(Translation.SC_ACTION_PRICE_MATCHED)
    ]
  }

  private getActions = (): ActionItem[] => {
    const { negotiationDetails, isCounterpartyNegotiation, enabledActionIds, isGroupRow } = this.props

    const warnings = this.getWarnings()

    const statusText = getLiveStatusText(negotiationDetails[0])
    const counterInText = Locale.translate(Translation.TC_COUNTER_IN)
    const isAcceptCounter = !isGroupRow && statusText === counterInText

    const isAcceptNew = negotiationDetails.some(
      negotiationDetail => !negotiationDetail.negotiation.quotedAtLeastOnce && (canAccept(negotiationDetail) || canSend(negotiationDetail))
    )

    const acceptAction = {
      id: Translation.TC_ACTION_ACCEPT,
      label: this.getLabel(
        isAcceptNew
          ? Translation.TC_ACTION_ACCEPT_NEW
          : isAcceptCounter
          ? Translation.TC_ACTION_ACCEPT_COUNTER
          : Translation.TC_ACTION_ACCEPT,
        negotiationDetails.filter(negotiationDetail => canAccept(negotiationDetail)).length
      ),
      type: isAcceptCounter ? SplitButtonType.Secondary : isAcceptNew ? SplitButtonType.Secondary : SplitButtonType.Good,
      onClick: this.handleAccept,
      hidden: shouldHideAccept(negotiationDetails),
      enabled: !isCounterpartyNegotiation && this.canAcceptMultiple(negotiationDetails)
    }

    const refreshAction = {
      id: Translation.TC_ACTION_REFRESH,
      label: this.getLabel(
        Translation.TC_ACTION_REFRESH,
        negotiationDetails.filter(negotiationDetail => canRefresh(negotiationDetail)).length
      ),
      type: SplitButtonType.Good,
      onClick: this.handleSend,
      enabled: negotiationDetails.every(negotiationDetail => canRefresh(negotiationDetail))
    }

    const actions: ActionItem[] = [
      {
        id: Translation.TC_ACTION_SEND,
        label: this.getLabel(Translation.TC_ACTION_SEND, negotiationDetails.filter(negotiationDetail => canSend(negotiationDetail)).length),
        type: SplitButtonType.Good,
        onClick: this.handleSend,
        enabled: negotiationDetails.every(negotiationDetail => canSend(negotiationDetail))
      },
      {
        id: Translation.TC_ACTION_UNDO,
        label: this.getLabel(
          Translation.TC_ACTION_UNDO,
          negotiationDetails.filter(negotiationDetail => canRevert(negotiationDetail)).length
        ),
        type: SplitButtonType.BadLight,
        onClick: this.handleRevert,
        enabled: negotiationDetails.some(negotiationDetail => canRevert(negotiationDetail))
      },
      {
        id: Translation.TC_ACTION_WITHDRAW,
        label: this.getLabel(
          Translation.TC_ACTION_WITHDRAW,
          negotiationDetails.filter(negotiationDetail => canWithdraw(negotiationDetail)).length
        ),
        type: SplitButtonType.BadOutline,
        onClick: this.handleWithdraw,
        enabled: negotiationDetails.every(negotiationDetail => canWithdraw(negotiationDetail))
      },
      {
        id: COUNTERPARTY_ACCEPT,
        label: negotiationDetails[0].counterparty
          ? Locale.translate(Translation.TC_ACTION_ACCEPT_COUNTERPARTY, {
              counterparty: TradingEntityWithAliasHelper.getName(negotiationDetails[0].counterparty)
            })
          : Translation.TC_ACTION_ACCEPT,
        type: SplitButtonType.Good,
        onClick: this.handleAccept,
        hidden: !this.props.isCounterpartyViewEnabled,
        enabled: this.props.isCounterpartyViewEnabled && isCounterpartyNegotiation && this.canAcceptMultiple(negotiationDetails),
        warnings
      },
      {
        id: Translation.TC_ACTION_REJECT,
        label: this.getLabel(
          Translation.TC_ACTION_REJECT,
          negotiationDetails.filter(negotiationDetail => canReject(negotiationDetail)).length
        ),
        onClick: this.handleReject,
        enabled: !isCounterpartyNegotiation && negotiationDetails.every(negotiationDetail => canReject(negotiationDetail))
      },
      {
        id: COUNTERPARTY_REJECT,
        buttonLabel: 'X',
        label: this.getLabel(
          Translation.TC_ACTION_REJECT,
          negotiationDetails.filter(negotiationDetail => canReject(negotiationDetail)).length
        ),
        type: SplitButtonType.BadSquare,
        onClick: this.handleReject,
        hidden: !this.props.isCounterpartyViewEnabled,
        enabled: isCounterpartyNegotiation && negotiationDetails.every(negotiationDetail => canReject(negotiationDetail)),
        warnings
      }
    ]

    if (!negotiationDetails[0].ownOrder) {
      actions.push({
        id: Translation.TC_ACTION_BULK_PRICE,
        label: this.getLabel(
          Translation.TC_ACTION_BULK_PRICE,
          negotiationDetails.filter(
            negotiationDetail => isQuotable(negotiationDetail.negotiation.status) && canUpdateAttributes(negotiationDetail)
          ).length
        ),
        onClick: this.handleBulkPrice,
        enabled:
          negotiationDetails.length > 1 &&
          negotiationDetails.every(
            negotiationDetail => isQuotable(negotiationDetail.negotiation.status) && canUpdateAttributes(negotiationDetail)
          )
      })
    } else {
      actions.push(
        {
          id: Translation.TC_ACTION_CANCEL_RFQ,
          label: this.getLabel(
            Translation.TC_ACTION_CANCEL_RFQ,
            negotiationDetails.filter(negotiationDetail => canCancel(negotiationDetail)).length
          ),
          type: SplitButtonType.BadLight,
          onClick: this.handleCancel,
          enabled: negotiationDetails.every(negotiationDetail => canCancel(negotiationDetail))
        },
        {
          id: Translation.TC_ACTION_BACK_TO_STAGING,
          label: this.getLabel(
            Translation.TC_ACTION_BACK_TO_STAGING,
            negotiationDetails.filter(negotiationDetail => canBackToStaging(negotiationDetail)).length
          ),
          onClick: this.handleBackToStaging,
          enabled: negotiationDetails.every(negotiationDetail => canBackToStaging(negotiationDetail))
        }
      )
    }

    actions.push({
      id: Translation.TC_ACTION_OPEN_TICKET,
      label: this.getLabel(Translation.TC_ACTION_OPEN_TICKET, negotiationDetails.length),
      onClick: this.handleOpenTicket,
      enabled: true
    })

    // those actions must be the last ones to add to the array
    if (this.shouldShowAcceptAsPrimaryAction(negotiationDetails)) {
      actions.unshift(acceptAction)
      negotiationDetails[0].ownOrder && actions.unshift(refreshAction)
    } else {
      actions.push(acceptAction)
      negotiationDetails[0].ownOrder && actions.push(refreshAction)
    }

    return enabledActionIds ? actions.filter(({ id }) => id && enabledActionIds.includes(id)) : actions
  }

  private shouldShowAcceptAsPrimaryAction = (negotiationDetails: NegotiationDetailWithMeta[]) => {
    const isOwnOrder = !!negotiationDetails[0].ownOrder
    const isMultipleSelection = negotiationDetails.length > 1

    const detail = negotiationDetails[0]

    const hasCollection = !!detail.collection?.id

    const isCollectionNameRow =
      negotiationDetails.length === 1 &&
      negotiationDetails[0].ownOrder &&
      negotiationDetails[0]?.collection?.id &&
      negotiationDetails[0].meta?.isCollectionSummary &&
      isSummaryNegotiationType(detail.negotiation?.negotiationType)

    const isBestQuotedOwnOrderNegotiationSummary = !!(
      negotiationDetails.length === 1 &&
      negotiationDetails[0].collection &&
      negotiationDetails[0].collection.id &&
      negotiationDetails[0].ownOrder &&
      isSummaryNegotiationType(negotiationDetails[0].negotiation?.negotiationType) &&
      negotiationDetails[0].meta?.isBestQuote === IsBestQuote.YES
    )

    const isGroupedByCollectionAndCounterparty = this.props.groupByOption === InitiatedGroupByOptions.COLLECTIONS_COUNTERPARTY

    const isOCOBestQuoteItem =
      negotiationDetails.length === 1 &&
      negotiationDetails[0].collection &&
      negotiationDetails[0].collection.id &&
      negotiationDetails[0].ownOrder &&
      negotiationDetails[0].negotiation.negotiationType === NegotiationType.ONE_CANCELS_OTHERS_ITEM &&
      negotiationDetails[0].meta?.isBestQuote === IsBestQuote.YES

    const isBestQuotedStandardItem =
      negotiationDetails[0]?.collection?.type === CollectionType.SEND_INDIVIDUALLY &&
      negotiationDetails[0].meta?.isBestQuote === IsBestQuote.YES

    const hasOwnOrderSummaryOneCounterparty = !!(
      negotiationDetails[0].collection?.id &&
      negotiationDetails[0].ownOrder &&
      (isSummaryNegotiationType(negotiationDetails[0]?.negotiation?.negotiationType) ||
        negotiationDetails[0]?.collection.type === CollectionType.SEND_INDIVIDUALLY) &&
      this.props.hasInitiatedCollectionOnlyOneCounterparty(negotiationDetails[0].collection.id)
    )

    const hasCollectionAnyBestPrice =
      negotiationDetails.length === 1 &&
      negotiationDetails[0].collection &&
      negotiationDetails[0].collection.id &&
      this.props.hasCollectionAnyBestPrice(negotiationDetails[0].collection.id)

    const isBestQuoteEligibleAndNotBestQuote = detail.meta?.isBestQuoteEligible && detail.meta.isBestQuote === IsBestQuote.NO

    return (
      (!isBestQuoteEligibleAndNotBestQuote &&
        (!isOwnOrder ||
          isMultipleSelection ||
          isCollectionNameRow ||
          !isGroupedByCollectionAndCounterparty ||
          !hasCollection ||
          !hasCollectionAnyBestPrice ||
          isBestQuotedOwnOrderNegotiationSummary ||
          isOCOBestQuoteItem ||
          isBestQuotedStandardItem ||
          hasOwnOrderSummaryOneCounterparty)) ||
      (isOwnOrder && this.props.isCounterpartyViewEnabled)
    )
  }

  private getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = this.props
    const formattedCount = isGroupRow ? ` (${count})` : ''
    return `${Locale.translate(baseLabel)}${formattedCount}`
  }

  private canAcceptMultiple = (negotiationDetails: NegotiationDetailWithMeta[]) => {
    const parentOrderIds = negotiationDetails.map(negotiationDetail => negotiationDetail.negotiation.parentOrderId)
    return (
      negotiationDetails.every(negotiationDetail => canAccept(negotiationDetail)) && parentOrderIds.length === new Set(parentOrderIds).size
    )
  }

  private handleSend = () => {
    const { onCounter, serverClientTimeOffset, onQuote, negotiationDetails } = this.props

    if (negotiationDetails[0].ownOrder) {
      negotiationDetails.forEach(negotiationDetail =>
        onCounter(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder, canRefresh(negotiationDetail))
      )
    } else {
      negotiationDetails.forEach(negotiationDetail =>
        onQuote(
          negotiationDetail.negotiation.negotiationId,
          negotiationDetail.ownOrder,
          getValidUntilTime(serverClientTimeOffset, negotiationDetail.otwDuration),
          negotiationDetail.otwDuration === 0
        )
      )
    }
  }

  private handleRevert = () => {
    const { onInitiatorRevert, onResponderRevert, negotiationDetails } = this.props

    negotiationDetails.forEach(negotiationDetail => {
      if (canRevert(negotiationDetail)) {
        if (negotiationDetail.ownOrder) {
          onInitiatorRevert(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
        } else {
          onResponderRevert(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
        }
      }
    })
  }

  private handleWithdraw = () => {
    const { onWithdraw, negotiationDetails } = this.props
    negotiationDetails.forEach(negotiationDetail => onWithdraw(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder))
  }

  private handleAccept = () => {
    const { onAccept, negotiationDetails } = this.props
    onAccept(
      negotiationDetails.map(negotiationDetail => {
        return {
          negotiationId: negotiationDetail.negotiation.negotiationId,
          negotiationSide: ownOrderToSide(negotiationDetail.ownOrder),
          quoteResponseId: negotiationDetail.negotiation.negotiationId
        }
      })
    )
  }

  private handleReject = () => {
    const { onReject, negotiationDetails } = this.props
    negotiationDetails.forEach(negotiationDetail => onReject(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder))
  }

  private handleBulkPrice = () => {
    const { negotiationDetails, showModal } = this.props
    showModal(<BulkPriceDialog negotiationIds={negotiationDetails.map(negotiationDetail => negotiationDetail.negotiation.negotiationId)} />)
  }

  private handleCancel = async () => {
    const { onCancelBulk, showDialog, negotiationDetails, getNegotiationCountWithCollectionId, getNegotiationCountWithRfqId } = this.props

    const canCancelAndStage = negotiationDetails.every(negotiationDetail => {
      const { collectionId, parentOrderId, negotiationType } = negotiationDetail.negotiation
      if (negotiationType === NegotiationType.CONTINGENT_ITEM) {
        return false
      }

      if (negotiationType === NegotiationType.INDIVIDUAL) {
        const totalNumberOfNegotiationsWithParentOrderId = getNegotiationCountWithRfqId(parentOrderId)

        return (
          negotiationDetails.filter(({ negotiation }) => negotiation.parentOrderId === parentOrderId).length ===
          totalNumberOfNegotiationsWithParentOrderId
        )
      }

      if (collectionId === null) {
        throw new Error('Null collection Id')
      }
      const totalNumberOfNegotiationsWithCollectionId = getNegotiationCountWithCollectionId(collectionId)

      return (
        negotiationDetails.filter(({ negotiation }) => negotiation.collectionId === collectionId).length ===
        totalNumberOfNegotiationsWithCollectionId
      )
    })

    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_CANCEL_RFQ,
      message: Translation.SC_CANCEL_RFQ_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, ...(canCancelAndStage ? [Translation.SC_BUTTON_YES_AND_STAGE] : []), Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      onCancelBulk(
        negotiationDetails.map(negotiationDetail => negotiationDetail.negotiation.negotiationId),
        false
      )
    }

    if (dialogResult === Translation.SC_BUTTON_YES_AND_STAGE) {
      onCancelBulk(
        negotiationDetails.map(negotiationDetail => negotiationDetail.negotiation.negotiationId),
        true
      )
    }
  }

  private handleBackToStaging = () => {
    const { onBackToStaging, negotiationDetails } = this.props
    negotiationDetails.forEach(negotiationDetail =>
      onBackToStaging(negotiationDetail.negotiation.negotiationId, negotiationDetail.ownOrder)
    )
  }

  private handleOpenTicket = () => {
    const { onOpenTicket, showModal, negotiationDetails } = this.props
    negotiationDetails.forEach(negotiationDetail => {
      if (isOpenFin()) {
        onOpenTicket(
          negotiationDetail.negotiation.negotiationId,
          EntityType.NEGOTIATION,
          getRFQTitle(negotiationDetail),
          negotiationDetail.ownOrder,
          isNegotiationHistory(negotiationDetail.negotiation)
        )
      } else {
        showModal(
          <Ticket
            negotiationId={negotiationDetail.negotiation.negotiationId}
            ownOrder={negotiationDetail.ownOrder}
            history={isNegotiationHistory(negotiationDetail.negotiation)}
          />
        )
      }
    })
  }
}

export const LiveActionsButton = withModal(connect(mapStateToProps, mapDispatchToProps)(LiveActionsButtonComponent))
